import React, { useState } from 'react'
import { addGiftVoucherToBasket } from '@lib/spektrix'
import BasketUpdateEvent from '@lib/basket-update-event'

const GiftVoucher = () => {
  const [amount, setAmount] = useState('')
  const [recipientName, setRecipientName] = useState('')
  const [senderName, setSenderName] = useState('')
  const [deliveryEmail, setDeliveryEmail] = useState('')
  const [deliveryType, setDeliveryType] = useState('OtherEmail') // 'CustomerEmail' or 'OtherEmail'
  const [sendDate, setSendDate] = useState('')
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  const commonAmounts = [25, 50, 75, 100, 150, 200]

  const handleSubmit = async e => {
    e.preventDefault()
    setError(null)
    setSuccess(false)

    // Validate form
    if (!amount || amount < 1) {
      setError('Please enter a valid amount')
      return
    }

    if (!recipientName) {
      setError('Please enter recipient name')
      return
    }

    if (!senderName) {
      setError('Please enter your name')
      return
    }

    if (deliveryType === 'OtherEmail' && !deliveryEmail) {
      setError('Please enter recipient email address')
      return
    }

    setLoading(true)

    try {
      // Create single voucher
      const vouchers = [
        {
          amount: parseFloat(amount),
          toName: recipientName,
          fromName: senderName,
          message: message || undefined,
          sendDate: sendDate || undefined,
          deliveryType: deliveryType,
          deliveryEmailAddress:
            deliveryType === 'OtherEmail' ? deliveryEmail : undefined
        }
      ]

      await addGiftVoucherToBasket(vouchers)

      // Dispatch basket update event
      window.dispatchEvent(new Event(BasketUpdateEvent))

      setSuccess(true)
      // Reset form
      setAmount('')
      setRecipientName('')
      setSenderName('')
      setDeliveryEmail('')
      setDeliveryType('OtherEmail')
      setSendDate('')
      setMessage('')

      // Auto-hide success message after 5 seconds
      setTimeout(() => setSuccess(false), 5000)
    } catch (err) {
      console.error('Error adding gift voucher:', err)
      setError(err.message || 'Failed to add gift voucher to basket')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='w-full p-3 sm: p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg'>
      <h2 className='text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6 !mt-0'>
        Purchase Gift Vouchers
      </h2>

      <form onSubmit={handleSubmit} className='space-y-6'>
        {/* Amount Selection */}
        <div>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
            Amount
          </label>
          <div className='grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3'>
            {commonAmounts.map(commonAmount => (
              <button
                key={commonAmount}
                type='button'
                onClick={() => setAmount(commonAmount.toString())}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  amount === commonAmount.toString()
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                £{commonAmount}
              </button>
            ))}
          </div>
          <div className='relative'>
            <span className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400'>
              £
            </span>
            <input
              type='number'
              min='1'
              step='0.01'
              value={amount}
              onChange={e => setAmount(e.target.value)}
              className='w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
              placeholder='Enter custom amount'
              required
            />
          </div>
        </div>

        {/* Names and Email */}
        <div className='space-y-4'>
          <h3 className='text-lg font-medium text-gray-900 dark:text-gray-100'>
            Gift Details
          </h3>
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
            <div>
              <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                Recipient Name
              </label>
              <input
                type='text'
                value={recipientName}
                onChange={e => setRecipientName(e.target.value)}
                className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                placeholder='Who is this for?'
                required
              />
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                Your Name
              </label>
              <input
                type='text'
                value={senderName}
                onChange={e => setSenderName(e.target.value)}
                className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                placeholder='Your name'
                required
              />
            </div>
          </div>
        </div>

        {/* Delivery Options */}
        <div className='space-y-3'>
          <h3 className='text-lg font-medium text-gray-900 dark:text-gray-100'>
            Delivery Details
          </h3>
          <div>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
              Who should receive the voucher?
            </label>
            <div className='grid grid-cols-1 sm:grid-cols-2 gap-2'>
              <label
                className={`relative flex items-center p-2.5 border-2 rounded-lg cursor-pointer transition-colors ${
                  deliveryType === 'OtherEmail'
                    ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
              >
                <input
                  type='radio'
                  name='deliveryType'
                  value='OtherEmail'
                  checked={deliveryType === 'OtherEmail'}
                  onChange={e => setDeliveryType(e.target.value)}
                  className='sr-only'
                />
                <div className='flex items-center'>
                  <div
                    className={`w-4 h-4 rounded-full border-2 mr-2.5 flex items-center justify-center flex-shrink-0 ${
                      deliveryType === 'OtherEmail'
                        ? 'border-blue-600'
                        : 'border-gray-400 dark:border-gray-500'
                    }`}
                  >
                    {deliveryType === 'OtherEmail' && (
                      <div className='w-2 h-2 rounded-full bg-blue-600' />
                    )}
                  </div>
                  <div>
                    <p className='font-medium text-gray-900 dark:text-white text-sm !my-0'>
                      Send to recipient
                    </p>
                    <p className='text-xs text-gray-600 dark:text-gray-400 mt-0.5 !mb-0'>
                      Email goes directly to them
                    </p>
                  </div>
                </div>
              </label>

              <label
                className={`relative flex items-center p-2.5 border-2 rounded-lg cursor-pointer transition-colors ${
                  deliveryType === 'CustomerEmail'
                    ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
              >
                <input
                  type='radio'
                  name='deliveryType'
                  value='CustomerEmail'
                  checked={deliveryType === 'CustomerEmail'}
                  onChange={e => setDeliveryType(e.target.value)}
                  className='sr-only'
                />
                <div className='flex items-center'>
                  <div
                    className={`w-4 h-4 rounded-full border-2 mr-2.5 flex items-center justify-center flex-shrink-0 ${
                      deliveryType === 'CustomerEmail'
                        ? 'border-blue-600'
                        : 'border-gray-400 dark:border-gray-500'
                    }`}
                  >
                    {deliveryType === 'CustomerEmail' && (
                      <div className='w-2 h-2 rounded-full bg-blue-600' />
                    )}
                  </div>
                  <div>
                    <p className='font-medium text-gray-900 dark:text-white text-sm !my-0'>
                      Send to me
                    </p>
                    <p className='text-xs text-gray-600 dark:text-gray-400 mt-0.5 !mb-0'>
                      I'll forward it myself
                    </p>
                  </div>
                </div>
              </label>
            </div>
          </div>

          {deliveryType === 'OtherEmail' && (
            <div>
              <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                Recipient Email Address
              </label>
              <input
                type='email'
                value={deliveryEmail}
                onChange={e => setDeliveryEmail(e.target.value)}
                className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                placeholder='Recipient email address'
                required
              />
              <p className='mt-1 text-sm text-gray-500 dark:text-gray-400 !m-0'>
                The voucher will be sent directly to the recipient
              </p>
            </div>
          )}
          {deliveryType === 'CustomerEmail' && (
            <div className='bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800'>
              <p className='text-sm text-blue-700 dark:text-blue-300 !m-0'>
                The voucher will be sent to your account email address
              </p>
            </div>
          )}
        </div>

        {/* Send Date */}
        <div>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
            Send Date (Optional)
          </label>
          <input
            type='date'
            value={sendDate}
            onChange={e => setSendDate(e.target.value)}
            min={new Date().toISOString().split('T')[0]}
            className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
          />
          <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
            Leave blank to send immediately, or choose a future date
          </p>
          <p className='mt-1 text-sm text-amber-600 dark:text-amber-400'>
            <svg
              className='inline-block w-4 h-4 mr-1'
              fill='currentColor'
              viewBox='0 0 20 20'
            >
              <path
                fillRule='evenodd'
                d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z'
                clipRule='evenodd'
              />
            </svg>
            Vouchers are valid for 12 months from the send date
          </p>
        </div>

        {/* Personal Message */}
        <div>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
            Personal Message (Optional)
          </label>
          <textarea
            rows={4}
            value={message}
            onChange={e => setMessage(e.target.value)}
            className='w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
            placeholder='Add a personal message to your gift voucher...'
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className='bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg'>
            {error}
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className='bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 px-4 py-3 rounded-lg'>
            Gift voucher added to basket successfully!
          </div>
        )}

        {/* Submit Button */}
        <button
          type='submit'
          disabled={loading}
          className={`w-full px-6 py-3 text-white font-medium rounded-lg transition-colors ${
            loading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 focus:ring-4 focus:ring-blue-500/50'
          }`}
        >
          {loading ? 'Adding to Basket...' : 'Add to Basket'}
        </button>
      </form>
    </div>
  )
}

export default GiftVoucher
